import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ComputedPageItem, HexColour, IntColour} from '@taopix/taopix-core/dist/types/types';
import {BorderIcon, ContentPanel} from '@taopix/taopix-design-system';
import {RangeWithValue} from '@taopix/taopix-design-system';
import {
  AssetType,
  BorderSize,
  BoxType,
  CornerSize,
  Colour as ColourUtils,
  Opacity,
  BoxTypeShape,
  NumBool,
} from '@taopix/taopix-core';
import {useTaopix} from '../../../Context/TaopixContext';
import classNames from 'classnames';
import {useExperience} from '../../../Experience/ExperienceProvider';
import Colour from './Colour';
import {Tile} from '../../../BackgroundsList/BackgroundsList';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import {getSafeColour} from '../../../../Utils/Colour';
import useSettings from '../../../../Hooks/useSettings';
import ToolPaletteSection from '../../../ToolPalette/ToolPaletteSection';
import PaletteInformationMessage from '../PaletteInformationMessage';

export type BorderProps = {
  sectionKey: string;
  item?: ComputedPageItem;
};

export type ShapeBorder = {
  borderSize: number;
  borderCornerSize: number;
  borderOpacity: number;
  borderColour: HexColour;
};

type BorderExperienceValues = {
  size: boolean;
  rounded: boolean;
  transparency: boolean;
  colour: boolean;
};

const Border = ({sectionKey, item}: BorderProps) => {
  const app = useTaopix();
  const settings = useSettings();
  const ui = useUiState();
  const {t} = useTranslation();

  const {experience} = useExperience();
  const {pictureBoxOptions, textBoxOptions, shapeBoxOptions, stickerOptions} = experience.EDITOR;

  const [showSection, setShowSection] = useState<boolean>(false);
  const [disableSection, setDisableSection] = useState<boolean>(false);
  const [notAvailableMessage, setNotAvailableMessage] = useState<string>('');

  const [selectedBorderColour, setSelectedBorderColour] = useState<string | number | undefined>(
    (ColourUtils.int2Hex(item?.stroke?.colour as IntColour) as HexColour).replace('#', '')
  );

  const colours = settings.state?.colours?.palette;

  const [shapeBorderValues, setShapeBorderValues] = useState<ShapeBorder>({
    borderSize: item?.stroke?.size as number,
    borderCornerSize: item?.cornersize as number,
    borderOpacity: item?.stroke?.alpha as number,
    borderColour: '#000000',
  });

  const [borderOptions, setBorderOptions] = useState<BorderExperienceValues>({
    size: true,
    rounded: true,
    transparency: true,
    colour: true,
  });

  useEffect(() => {
    const colour = ColourUtils.int2Hex(item?.stroke?.colour as IntColour) as HexColour;
    if (item?.stroke?.active === 0) {
      setShapeBorderValues({
        borderSize: 0,
        borderCornerSize: item?.cornersize as number,
        borderOpacity: item?.stroke?.alpha as number,
        borderColour: colour,
      });
    } else {
      setShapeBorderValues({
        borderSize: item?.stroke?.size as number,
        borderCornerSize: item?.cornersize as number,
        borderOpacity: item?.stroke?.alpha as number,
        borderColour: colour,
      });
    }
    setSelectedBorderColour(colour.replace('#', ''));
  }, [item?.ref, item?.stroke]);

  useEffect(() => {
    if (selectedBorderColour !== '' && selectedBorderColour !== undefined) {
      if (
        item?.ref !== -1 &&
        selectedBorderColour !== getSafeColour(item?.stroke?.colour as IntColour)
      ) {
        app.core.dispatch('SHAPE.SET_BORDER_COLOUR', `#${selectedBorderColour}`).catch(error => {
          console.error(error);
        });
      }
    }
  }, [selectedBorderColour]);

  const isActiveInExperience = (item: ComputedPageItem) => {
    const boxType = item.type;
    // Return whether or not this option is enabled in the experience for the selected item
    if (
      boxType === BoxType.Image &&
      item?.asset?.assetType !== AssetType.Background &&
      item?.asset?.assetType !== AssetType.ScrapbookImage
    ) {
      setBorderOptions({
        size: pictureBoxOptions.borderSize,
        rounded: [BoxTypeShape.Oval, BoxTypeShape.Polygon].includes(item?.shape)
          ? false
          : pictureBoxOptions.borderSize,
        transparency: pictureBoxOptions.borderTransparency,
        colour: pictureBoxOptions.borderColour,
      });
      return pictureBoxOptions.border;
    }
    if (boxType === BoxType.Text) {
      setBorderOptions({
        size: textBoxOptions.borderSize,
        rounded: false,
        transparency: textBoxOptions.borderTransparency,
        colour: textBoxOptions.borderColour,
      });
      return textBoxOptions.border;
    }
    if (
      boxType === BoxType.Shape &&
      [BoxTypeShape.Line, BoxTypeShape.Oval, BoxTypeShape.Rect].includes(item?.shape)
    ) {
      // rect, oval - all options
      // line - only size
      // polygon - no options
      if (BoxTypeShape.Line === item?.shape) {
        setBorderOptions({
          size: shapeBoxOptions.borderSize,
          rounded: false,
          transparency: false,
          colour: false,
        });
      } else if (BoxTypeShape.Oval === item?.shape) {
        setBorderOptions({
          size: shapeBoxOptions.borderSize,
          rounded: false,
          transparency: shapeBoxOptions.borderTransparency,
          colour: shapeBoxOptions.borderColour,
        });
      } else {
        setBorderOptions({
          size: shapeBoxOptions.borderSize,
          rounded: shapeBoxOptions.roundedCorners,
          transparency: shapeBoxOptions.borderTransparency,
          colour: shapeBoxOptions.borderColour,
        });
      }
      return shapeBoxOptions.border;
    }
    if (AssetType.ScrapbookImage === item.hiresfiletype) {
      setBorderOptions({
        size: stickerOptions.borderSize,
        rounded: stickerOptions.roundedCorners,
        transparency: stickerOptions.borderTransparency,
        colour: stickerOptions.borderColour,
      });
      return stickerOptions.border;
    } else {
      return false;
    }
  };

  // Check if we can show this option based on the selected item type, its protection, the experience and canEditPageLayout
  useEffect(() => {
    if (item && item.ref !== -1) {
      // If there is a selected item
      if (isActiveInExperience(item)) {
        // Show the button
        setShowSection(true);
        // Set whether the button should be disabled or not
        setDisableSection(
          NumBool.True === item?.protectcontents ||
            (item.errors?.imageMissing as boolean) ||
            ui.state.swapActive
        );
      } else {
        // If this palette is not enabled for the selected item, hide the toolpalette button and show the 'not available for the selected item' message
        setShowSection(false);
        setNotAvailableMessage(t('toolpalette:str_OptionNotAvailableForSelectedItem'));
      }
    } else {
      // If no item is selected, hide the button and show the 'please select an item' message
      setShowSection(false);
      setNotAvailableMessage(t('toolpalette:str_MessageSelectAnItemToViewOptions'));
    }
  }, [
    item?.asset,
    item?.asset?.assetType,
    item?.type,
    item?.image,
    item?.shape,
    ui.state.swapActive,
  ]);

  const sectionButtonClasses = classNames('lg:py-md');

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'px-md',
    'space-y-sm',
    'lg:flex-1',
    'lg:pb-lg',
    'sm:pb-md',
    'sm:h-mobilePaletteContentHeight',
    'sm:max-w-full',
    'overflow-y-auto'
  );

  if (
    ui.state.palette.section !== 'box-settings-palette' &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}` &&
    ui.state.palette.section !== 'box-settings-palette/border/border-colour'
  ) {
    return null;
  }

  return (
    <ToolPaletteSection
      id={'boxSettingsBorderPalette'}
      title={t('boxpalettes:str_TitleBorder')}
      buttonIcon={<BorderIcon />}
      sectionKey={sectionKey}
      buttonClasses={sectionButtonClasses}
      subSection={true}
      useFocusMode
      disabled={disableSection}
      showButton={showSection}
      buttonLayout="horizontal"
      parentselected={true}
    >
      <div className={wrapperClasses}>
        {!showSection || disableSection ? (
          // If the palette is not allowed by the experience, or is disabled by the box protection, show the relevant message
          // We show this message rather than closing/hiding the palette to avoid abrupt UI changes when no item or an invalid item is selected
          <PaletteInformationMessage message={notAvailableMessage} />
        ) : (
          // Only show the sub options if the palette is allowed in the experience and the content is not protected
          <>
            {borderOptions.size && (
              <ContentPanel>
                <RangeWithValue
                  data-testid={'box-settings-border-size-slider'}
                  key={`border-borderSize-${item?.ref}`}
                  label={t('boxpalettes:str_TitleBorderSize')}
                  value={shapeBorderValues.borderSize}
                  id={'border-size-slider'}
                  min={0}
                  max={BorderSize.Max}
                  submitOnBlur={true}
                  onValueChange={(value: number, interacting?: boolean) => {
                    // turn border off if value is 0
                    if (value === 0) {
                      app.core.dispatch('SHAPE.TOGGLE_BORDER', value).catch(error => {
                        console.error(error);
                      });
                    }
                    // only dispatch action is the value is greater than 0
                    if (value > 0 && item?.ref && item?.ref !== -1) {
                      app.core
                        .dispatch('SHAPE.UPDATE_BORDER_SIZE', value, interacting)
                        .catch(error => {
                          console.error(error);
                        });
                    }
                  }}
                  isSmallScreen={ui.state.isSmallScreen}
                />
              </ContentPanel>
            )}
            {borderOptions.rounded && (
              <ContentPanel>
                <RangeWithValue
                  data-testid={'box-settings-border-roundedcorners-slider'}
                  key={`border-borderCornerSize-${item?.ref}`}
                  label={t('boxpalettes:str_TitleRoundedCorners')}
                  value={shapeBorderValues.borderCornerSize}
                  id={'border-corner-slider'}
                  min={CornerSize.Min}
                  max={CornerSize.Max}
                  submitOnBlur={true}
                  onValueChange={(value: number, interacting?: boolean) => {
                    if (item?.ref && item?.ref !== -1) {
                      app.core
                        .dispatch('SHAPE.UPDATE_CORNER_SIZE', value, interacting)
                        .catch(error => {
                          console.error(error);
                        });
                    }
                  }}
                  isSmallScreen={ui.state.isSmallScreen}
                />
              </ContentPanel>
            )}
            {borderOptions.transparency && (
              <ContentPanel>
                <RangeWithValue
                  data-testid={'box-settings-border-transparency-slider'}
                  key={`border-borderOpacity-${item?.ref}`}
                  label={t('boxpalettes:str_TitleTransparency')}
                  value={shapeBorderValues.borderOpacity}
                  id={'border-opacity-slider'}
                  min={Opacity.Min}
                  max={Opacity.Max}
                  submitOnBlur={true}
                  onValueChange={(value: number, interacting?: boolean) => {
                    if (item?.ref && item?.ref !== -1) {
                      app.core
                        .dispatch('SHAPE.UPDATE_BORDER_OPACITY', value, interacting)
                        .catch(error => {
                          console.error(error);
                        });
                    }
                  }}
                  isSmallScreen={ui.state.isSmallScreen}
                />
              </ContentPanel>
            )}

            {ui.state?.palette?.section?.includes('border') && borderOptions.colour && (
              <ContentPanel>
                <Colour
                  id={'box-settings-border-colour-picker'}
                  data-testid={'box-settings-border-colour-picker'}
                  parentSection={'border'}
                  buttonLabel={t('*:str_LabelColour')}
                  sectionTitle={t('boxpalettes:str_LabelBorderColour')}
                  sectionKey={'border-colour'}
                  item={item}
                  colours={colours}
                  selectedColour={selectedBorderColour}
                  preventManualUncheck
                  useFocusMode={ui.state.isSmallScreen}
                  onClick={(
                    event: React.MouseEvent | React.ChangeEvent,
                    newSelectedBackground: Tile
                  ) => {
                    if (selectedBorderColour === newSelectedBackground?.src) {
                      setSelectedBorderColour('000000');
                    } else {
                      setSelectedBorderColour(newSelectedBackground?.src);
                    }
                  }}
                />
              </ContentPanel>
            )}
          </>
        )}
      </div>
    </ToolPaletteSection>
  );
};

export default Border;
